import {
  sqConditionRuntimeStore,
  sqItemRuntimeStore,
  sqScalarRuntimeStore,
  sqSeriesRuntimeStore,
  sqSummaryReportStore,
  sqTableRuntimeStore,
  sqTrendScalingColumnStore,
  sqTrendStore,
} from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { takeCapsulesFromSelection } from '@/tools/manualCondition/capsuleGroup.utilities';
import { findItemIn, getTrendStores } from '@/trend/trendDataHelper.utilities';
import {
  recomputeRuntimeChartItems,
  removeRuntimeCapsuleItems,
  writeRuntimeColor,
  writeRuntimeCustomizations,
  writeRuntimeProperties,
  writeRuntimeSelectionEffects,
  writeRuntimeStitchDetails,
} from '@/trendData/capsuleRuntime.actions';
import { recordDataStatus } from '@/trendData/itemRuntime.utilities';
import { rebuildSeriesRenderData, recolorSeriesZones } from '@/trendData/seriesRuntime.actions';
import { resolveCapsuleItem } from '@/trendData/trendCapsule.selectors';
import { ITEM_DATA_STATUS, ITEM_TYPES, TREND_VIEWS } from '@/trendData/trendData.constants';
import type { PushOption } from '@/core/flux.service';
import type { Item } from '@/trendData/itemStore.utilities';
import type { CustomizationPayload, DataStatusPayload } from '@/trendData/itemStoreBase.types';
import type { SelectedCapsule } from '@/trendData/trendData.types';

/**
 * Explicit callers for base item events whose target store is NOT statically knowable; an
 * arbitrary item id or a mixed collection. When the affected store IS known, use the
 * store-targeted setter in that store's actions file instead (e.g. `setSeriesDataStatusLoading`).
 *
 * Specifically a function to work around import ordering issues
 */
function getConvertedTrendItemStores() {
  return [sqTrendScalingColumnStore, sqSummaryReportStore];
}

/**
 * Runtime stores whose fetched data is discarded whole, for an item id whose type is not
 * statically knowable. Only the store owning that item's type has an entry, so the others no-op.
 * The series runtime store is handled explicitly at each site: a terminal status empties only its
 * drawable data, keeping the samples and string metadata so a string signal's axis survives a
 * failed refetch.
 *
 * TODO CRAB-57427: give every fetched-data store `clearValues` (removal) and
 * `clearDataForTerminalStatus` (fetch ended without data) so each event is one loop over one list
 * and the per-store semantics live in the stores, mirroring the flux base-store method.
 */
function getFetchedDataStores() {
  return [sqScalarRuntimeStore, sqTableRuntimeStore];
}

export function removeItemsFromTrendStores(
  payload: { items: Array<Pick<Item, 'id'>>; numPixels?: number },
  ...option: [PushOption?]
): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.removeItems(payload));
  payload.items.forEach(({ id }) => {
    getFetchedDataStores().forEach((store) => store.actions.clearValues(id));
    sqSeriesRuntimeStore.actions.clearValues(id);
    sqConditionRuntimeStore.actions.clearValues(id);
    sqItemRuntimeStore.actions.clearItem(id);
  });
  flux.dispatch('TREND_REMOVE_ITEMS', payload, ...option);
  removeRuntimeCapsuleItems(payload);
  takeCapsulesFromSelection();
}

export function swapItemsInTrendStores(payload: { swaps: Record<string, string> }, ...option: [PushOption?]): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.swapItems(payload));
  getFetchedDataStores().forEach((store) => store.actions.swapValues(payload.swaps));
  sqSeriesRuntimeStore.actions.swapValues(payload.swaps);
  sqItemRuntimeStore.actions.swapItems(payload.swaps);
  flux.dispatch('TREND_SWAP_ITEMS', payload, ...option);
}

/**
 * A capsule is also resolved against the capsule dataset as `resolvedItem`, which only the capsule
 * store's selection handler reads; the other stores receive the item as the caller supplied it.
 */
export function setSelectedInTrendStores(
  payload: { item: Item; selected: boolean; numPixels?: number },
  ...option: [PushOption?]
): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.setSelected(payload));
  const fluxPayload =
    payload.item.itemType === ITEM_TYPES.CAPSULE
      ? { ...payload, resolvedItem: resolveCapsuleItem(payload.item as SelectedCapsule) }
      : payload;
  flux.dispatch('TREND_SET_SELECTED', fluxPayload, ...option);
  writeRuntimeSelectionEffects(payload);
  takeCapsulesFromSelection();
}

export function setSelectedCapsulesInTrendStores(
  payload: {
    capsules: Array<Pick<Item, 'id'>>;
    selected: boolean;
    numPixels?: number;
  },
  ...option: [PushOption?]
): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.setSelectedCapsules(payload));
  const resolvedCapsules = payload.capsules.map((capsule) => resolveCapsuleItem(capsule));
  flux.dispatch('TREND_SET_SELECTED_CAPSULES', { ...payload, resolvedCapsules }, ...option);
  if (payload.capsules.length > 0) {
    if (sqTrendStore.view === TREND_VIEWS.CHAIN) {
      writeRuntimeStitchDetails(payload.numPixels ?? 0);
    }
    recomputeRuntimeChartItems();
  }
  takeCapsulesFromSelection();
}

export function setColorInTrendStores(
  payload: { id: string; color: string | undefined },
  ...option: [PushOption?]
): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.setColor(payload));
  flux.dispatch('TREND_SET_COLOR', payload, ...option);
  writeRuntimeColor(payload.id, payload.color);
  recolorSeriesZones(payload.id, payload.color);
}

export function setRequestProgressInTrendStores(
  payload: {
    id: string;
    progress?: number;
    timingInformation?: string;
    meterInformation?: string;
  },
  ...option: [PushOption?]
): void {
  if (findItemIn(getTrendStores(), payload.id)) {
    sqItemRuntimeStore.actions.setRequestProgress(payload.id, payload);
  }
  flux.dispatch('REQUEST_SET_PROGRESS', payload, ...option);
}

export function setPropertiesInTrendStores(
  payload: { id: string; includeChildren?: boolean } & Record<string, unknown>,
  ...option: [PushOption?]
): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.setProperties(payload));
  flux.dispatch('TREND_SET_PROPERTIES', payload, ...option);
  writeRuntimeProperties(payload);
}

export function setCustomizationsInTrendStores(
  payload: { items: CustomizationPayload[] },
  ...option: [PushOption?]
): void {
  getConvertedTrendItemStores().forEach((store) => store.actions.setCustomizations(payload));
  flux.dispatch('TREND_SET_CUSTOMIZATIONS', payload, ...option);
  writeRuntimeCustomizations(payload.items);
  rebuildSeriesRenderData(payload);
}

export function setDataStatusLoadingInTrendStores(id: string): void {
  recordDataStatus(ITEM_DATA_STATUS.LOADING, { id });
}

export function setDataStatusPresentInTrendStores(payload: DataStatusPayload): void {
  recordDataStatus(ITEM_DATA_STATUS.PRESENT, payload);
}

export function setDataStatusNotRequiredInTrendStores(id: string, ...option: [PushOption?]): void {
  const payload = { id };
  recordDataStatus(ITEM_DATA_STATUS.NOT_REQUIRED, payload);
  getConvertedTrendItemStores().forEach((store) => store.actions.clearDataForTerminalStatus(payload));
  getFetchedDataStores().forEach((store) => store.actions.clearValues(payload.id));
  sqSeriesRuntimeStore.actions.clearData(payload.id);
  flux.dispatch('TREND_SET_DATA_STATUS_NOT_REQUIRED', payload, ...option);
}

export function setDataStatusCanceledInTrendStores(id: string): void {
  recordDataStatus(ITEM_DATA_STATUS.CANCELED, { id });
}

export function setDataStatusCanceledRetriesInTrendStores(id: string): void {
  recordDataStatus(ITEM_DATA_STATUS.CANCELED_RETRIES, { id });
}

export function setDataStatusFailureInTrendStores(payload: DataStatusPayload, ...option: [PushOption?]): void {
  recordDataStatus(ITEM_DATA_STATUS.FAILURE, payload);
  getConvertedTrendItemStores().forEach((store) => store.actions.clearDataForTerminalStatus(payload));
  getFetchedDataStores().forEach((store) => store.actions.clearValues(payload.id));
  sqSeriesRuntimeStore.actions.clearData(payload.id);
  flux.dispatch('TREND_SET_DATA_STATUS_FAILURE', payload, ...option);
}

export function setDataStatusRedactedInTrendStores(payload: DataStatusPayload, ...option: [PushOption?]): void {
  recordDataStatus(ITEM_DATA_STATUS.REDACTED, payload);
  getConvertedTrendItemStores().forEach((store) => store.actions.clearDataForTerminalStatus(payload));
  getFetchedDataStores().forEach((store) => store.actions.clearValues(payload.id));
  sqSeriesRuntimeStore.actions.clearData(payload.id);
  flux.dispatch('TREND_SET_DATA_STATUS_REDACTED', payload, ...option);
}

export function setDataStatusAbortedInTrendStores(payload: DataStatusPayload, ...option: [PushOption?]): void {
  recordDataStatus(ITEM_DATA_STATUS.ABORTED, payload);
  getConvertedTrendItemStores().forEach((store) => store.actions.clearDataForTerminalStatus(payload));
  getFetchedDataStores().forEach((store) => store.actions.clearValues(payload.id));
  sqSeriesRuntimeStore.actions.clearData(payload.id);
  flux.dispatch('TREND_SET_DATA_STATUS_ABORTED', payload, ...option);
}
